import { useContext, useEffect, useState } from "react";
import State from "../../../../../../context";
import { Flex, Input, Skeleton, theme, Button } from "antd";
import ModalLevelProductDetails from "../../../admin/component/ModalLevelProductDetails";
import FiltersContainer from "./components/FiltersContainer";
import SecurityGate from "../../../../../../provider/SecurityGate";
import TableProducts from "./components/TableProducts";

import StylesContainer from "./style";

import {
  addFavoriteFinancialProduct,
  removeFavoriteFinancialProduct,
  addFavoriteSecurities,
  removeFavoriteSecurities,
} from "../../request";
import { getVisibleProducts, isPermissionReadonly } from "../../helper";

import { SearchOutlined } from "@ant-design/icons";

const PageProductsPrimary = ({
  companyFilters,
  handleActionClick,
  hideColumnAction,
  hideHeader,
  listProductsActive,
  proposalData,
  showEditInvestments,
}) => {
  const [state] = useContext(State);
  const { token } = theme.useToken();
  const [searchTerm, setSearchTerm] = useState("");
  const [assetManagerFilters, setAssetManagerFilters] = useState([]);
  const [customFilters, setCustomFilters] = useState([]);
  const [typeCategoryFilters, setTypeCategoryFilters] = useState([]);
  const [broadCategoryFilters, setBroadCategoryFilters] = useState([]);
  const [productFiltersApplied, setProductFiltersApplied] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const productsList = state.productsList?.map(it => ({
    ...it,
    customFilter:
      state.favoriteFinancialProducts?.includes(it._id) ||
      state.favoriteSecurities?.includes(it._id) // flag for custom filter
        ? "My Favorites"
        : null,
  }));

  useEffect(() => {
    // update default filter values, if custom items saved in the DB
    let savedFilters = companyFilters ?? state.advisorProducts;

    setAssetManagerFilters(
      savedFilters?.assets?.filter(it => it.enabled)?.map(it => it.name) ?? []
    );
    setTypeCategoryFilters(
      savedFilters?.types?.filter(it => it.enabled)?.map(it => it.name) ?? []
    );
    setBroadCategoryFilters(
      savedFilters?.categories?.filter(it => it.enabled)?.map(it => it.name) ??
        []
    );
  }, [companyFilters, state.advisorProducts]);

  // Set filtersApplied to true if any filters are applied
  useEffect(() => {
    if (
      searchTerm === "" &&
      customFilters.length === 0 &&
      typeCategoryFilters.length === 0 &&
      assetManagerFilters.length === 0 &&
      broadCategoryFilters.length === 0
    ) {
      setProductFiltersApplied(false);
    } else {
      setProductFiltersApplied(true);
    }
    setFilteredProducts(getFilteredProducts());
  }, [
    state.productsList,
    searchTerm,
    typeCategoryFilters,
    assetManagerFilters,
    broadCategoryFilters,
    customFilters,
  ]);

  const handleAssetManagerFilterChange = checkedValues => {
    setAssetManagerFilters(checkedValues);
  };

  const handleCustomFilterChange = checkedValues => {
    setCustomFilters(checkedValues);
  };

  const handleBroadCategoryFilterChange = checkedValues => {
    setBroadCategoryFilters(checkedValues);
  };

  const handleTypeCategoryFilterChange = checkedValues => {
    setTypeCategoryFilters(checkedValues);
  };

  const handleCardClick = cardId => {
    state.setKeyValue("productDetailsId", cardId);
    state.setKeyValue("openModalProductDetails", true);
  };

  const toggleFavorite = productId => {
    const isEtfProduct =
      state.productsList.find(it => it._id === productId)?.Type === "ETF";

    if (
      state.favoriteFinancialProducts?.includes(productId) ||
      state.favoriteSecurities?.includes(productId)
    ) {
      (isEtfProduct
        ? removeFavoriteSecurities
        : removeFavoriteFinancialProduct)({
        productId,
        userEmail: state._id,
      })
        .then(() =>
          state
            .setUserData()
            .then(adminData => {
              !adminData.favoriteFinancialProducts &&
                state.setKeyValue("favoriteFinancialProducts", []);
              state.showSuccess("Product removed from Favorite");
            })
            .catch(state.showError)
        )
        .catch(state.showError);
    } else {
      (isEtfProduct ? addFavoriteSecurities : addFavoriteFinancialProduct)({
        productId,
        userEmail: state._id,
      })
        .then(() =>
          state
            .setUserData()
            .then(() => state.showSuccess("Product marked as Favorite"))
            .catch(state.showError)
        )
        .catch(state.showError);
    }
  };

  const applyFilter = (products, key, filters) => {
    if (filters.length === 0) return products;
    return products?.filter(product => filters.includes(product[key]));
  };

  const applyFilterCustom = products => {
    if (customFilters.length === 0) return products;

    return applyFilter(products, "customFilter", customFilters);
  };

  const applyFilterSearch = products => {
    if (!searchTerm) return products;

    return products?.filter(
      product =>
        product.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        product.ticker?.toLowerCase()?.includes(searchTerm.toLowerCase())
    );
  };

  const getFilteredProducts = () =>
    applyFilter(
      applyFilter(
        applyFilter(
          applyFilterCustom(applyFilterSearch(productsList)),
          "assetManager",
          assetManagerFilters
        ),
        "broadCategory",
        broadCategoryFilters
      ),
      "type",
      typeCategoryFilters
    );

  return (
    <SecurityGate state={state}>
      <Skeleton active loading={state.loading}>
        <StylesContainer token={token}>
          {!hideHeader && (
            <header>
              <h4>All Products</h4>
            </header>
          )}

          <main>
            {!!productsList?.length && (
              <>
                <FiltersContainer
                  customFilters={customFilters}
                  productFiltersApplied={productFiltersApplied}
                  assetManagerFilters={assetManagerFilters}
                  broadCategoryFilters={broadCategoryFilters}
                  typeCategoryFilters={typeCategoryFilters}
                  handleCustomFilterChange={handleCustomFilterChange}
                  handleAssetManagerFilterChange={
                    handleAssetManagerFilterChange
                  }
                  handleBroadCategoryFilterChange={
                    handleBroadCategoryFilterChange
                  }
                  handleTypeCategoryFilterChange={
                    handleTypeCategoryFilterChange
                  }
                  productsList={productsList}
                  filteredProducts={filteredProducts}
                />

                <Flex
                  gap={16}
                  style={{ paddingRight: 10, width: "100%" }}
                  vertical
                >
                  <Flex justify="space-between">
                    <Input
                      allowClear
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="Search..."
                      size="large"
                      style={{ borderRadius: 20, width: 270 }}
                      suffix={
                        <SearchOutlined style={{ color: "rgba(0,0,0,.45)" }} />
                      }
                      value={searchTerm}
                    />

                    {showEditInvestments && (
                      <>
                        <Flex
                          align="center"
                          gap={4}
                          style={{
                            background: "#F8F8F8",
                            borderRadius: 12,
                            padding: "8px 16px",
                          }}
                        >
                          Investments enabled for advisors:
                          <b style={{ fontSize: 19 }}>
                            {
                              getVisibleProducts({
                                filters:
                                  companyFilters ?? state.advisorProducts,
                                productsList: state.productsList,
                              })?.length
                            }
                          </b>
                        </Flex>
                        <Button
                          disabled={isPermissionReadonly(
                            state.permissions?.accessLevel
                          )}
                          onClick={() =>
                            state.setKeyValue(
                              "openModalEnableInvestments",
                              true
                            )
                          }
                          shape="round"
                          type="primary"
                        >
                          Edit Investments
                        </Button>
                      </>
                    )}
                  </Flex>

                  <TableProducts
                    favoriteFinancialProducts={[
                      ...(state.favoriteFinancialProducts ?? []),
                      ...(state.favoriteSecurities ?? []),
                    ]}
                    filteredProducts={filteredProducts}
                    handleActionClick={handleActionClick}
                    handleCardClick={handleCardClick}
                    handleToggleFavorite={toggleFavorite}
                    hideColumnAction={hideColumnAction}
                    loading={state.loading}
                    listProductsActive={listProductsActive}
                  />
                </Flex>
              </>
            )}
          </main>
        </StylesContainer>
      </Skeleton>

      <ModalLevelProductDetails
        handleClose={() => state.setKeyValue("openModalProductDetails", false)}
        investmentAssumptions={proposalData ?? state.investmentAssumptions}
        open={state.openModalProductDetails}
        product={state.productsList?.find(
          it => it._id === state.productDetailsId
        )}
        showError={state.showError}
      />
    </SecurityGate>
  );
};

export default PageProductsPrimary;
